import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Reveal from "@/components/Reveal";
import Isotipo from "@/components/Isotipo";
import Seo from "@/components/Seo";
import { PROJECTS, UI } from "@/data/site";
import { useLang } from "@/context/LanguageContext";

const ProyectoDetalle = () => {
  const { slug } = useParams();
  const { t, lp } = useLang();
  const project = PROJECTS.find((p) => p.slug === slug);

  if (!project) {
    return (
      <div className="pt-40 pb-40 text-center" data-testid="project-not-found">
        <h1 className="font-display text-4xl font-black text-white">404</h1>
        <Link to={lp("/proyectos")} className="mt-6 inline-flex items-center gap-2 text-brand-gold font-display font-bold">
          <ArrowLeft size={18} /> {t(UI.cta.allProjects)}
        </Link>
      </div>
    );
  }

  const accent = project.branch === "audiovisual" ? "#edaa51" : "#26775e";
  const keys = t(project.keys);
  const idx = PROJECTS.findIndex((p) => p.slug === slug);
  const next = PROJECTS[(idx + 1) % PROJECTS.length];

  return (
    <div data-testid="proyecto-detalle-page">
      <Seo
        title={`${project.title} | Grabarte`}
        description={t(project.summary)}
        image={project.image}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          name: project.title,
          description: t(project.summary),
          image: project.image,
          creator: { "@type": "Organization", name: "Grabarte" },
        }}
      />
      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden pt-[72px]">
        <img src={project.image} alt={project.title} className="absolute inset-0 w-full h-full object-cover opacity-65 kenburns" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16 w-full">
          <Reveal>
            <Link to={lp("/proyectos")} className="inline-flex items-center gap-2 text-white/60 hover:text-white font-display text-xs font-bold uppercase tracking-widest mb-8 transition-colors" data-testid="back-to-projects">
              <ArrowLeft size={16} /> {t(UI.cta.allProjects)}
            </Link>
            <span className="block font-display text-xs font-bold uppercase tracking-[0.25em] mb-4" style={{ color: accent }}>{t(project.kicker)}</span>
            <h1 className="font-display text-5xl md:text-7xl font-black text-white leading-[0.95] max-w-4xl">{project.title}</h1>
          </Reveal>
        </div>
      </section>

      {/* Meta + body */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4 space-y-8">
          <Reveal>
            <div>
              <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-2">{t({ es: "Cliente", en: "Client" })}</p>
              <p className="font-body text-white/85">{project.client}</p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div>
              <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-2">{t({ es: "Servicios", en: "Services" })}</p>
              <p className="font-body text-white/85">{t(project.services)}</p>
            </div>
          </Reveal>
          {project.vimeo && (
            <Reveal delay={0.16}>
              <a href={`https://vimeo.com/${project.vimeo}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest" style={{ color: accent }} data-testid="vimeo-external-link">
                {t({ es: "Ver en Vimeo", en: "Watch on Vimeo" })} <ArrowRight size={16} />
              </a>
            </Reveal>
          )}
          {project.youtube && (
            <Reveal delay={0.16}>
              <a href={`https://www.youtube.com/watch?v=${project.youtube}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest" style={{ color: accent }} data-testid="youtube-external-link">
                {t({ es: "Ver en YouTube", en: "Watch on YouTube" })} <ArrowRight size={16} />
              </a>
            </Reveal>
          )}
        </div>

        <div className="lg:col-span-8">
          <Reveal>
            <p className="font-display text-2xl md:text-3xl font-bold text-white leading-snug text-balance">{t(project.summary)}</p>
            <p className="mt-8 text-white/60 font-body text-lg leading-relaxed">{t(project.body)}</p>
          </Reveal>

          {keys && keys.length > 0 && (
            <Reveal delay={0.1}>
              <div className="mt-12 space-y-4">
                <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white/40">{t({ es: "Claves del proyecto", en: "Project keys" })}</p>
                {keys.map((k, i) => (
                  <div key={i} className="flex items-start gap-4 border-t border-white/10 pt-4">
                    <Check size={18} style={{ color: accent }} className="mt-1 shrink-0" />
                    <p className="font-body text-white/80">{k}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          )}

          {project.festivals && project.festivals.length > 0 && (
            <Reveal delay={0.12}>
              <div className="mt-12 space-y-3" data-testid="festivals-list">
                <p className="font-display text-xs font-bold uppercase tracking-[0.2em]" style={{ color: accent }}>{t({ es: "Festivales", en: "Festivals" })}</p>
                {project.festivals.map((f, i) => (
                  <div key={i} className="flex items-start gap-4 border-t border-white/10 pt-3">
                    <Isotipo className="w-4 h-4 mt-1 shrink-0" fill={accent} />
                    <p className="font-body text-white/80">{f}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* YouTube embed */}
      {project.youtube && (
        <section className="max-w-6xl mx-auto px-6 pb-20">
          <Reveal>
            <div className="rounded-2xl overflow-hidden border border-white/10 aspect-video bg-black">
              <iframe
                title={project.title}
                src={`https://www.youtube.com/embed/${project.youtube}`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="youtube-embed"
              />
            </div>
          </Reveal>
        </section>
      )}

      {/* Vimeo embed */}
      {project.vimeo && (
        <section className="max-w-6xl mx-auto px-6 pb-20">
          <Reveal>
            <div className="rounded-2xl overflow-hidden border border-white/10 aspect-video bg-black">
              <iframe
                title={project.title}
                src={`https://player.vimeo.com/video/${project.vimeo}?title=0&byline=0&portrait=0`}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                data-testid="vimeo-embed"
              />
            </div>
          </Reveal>
        </section>
      )}

      {/* Next project */}
      <section className="border-t border-white/10">
        <Link to={lp(`/proyectos/${next.slug}`)} className="group block max-w-7xl mx-auto px-6 py-16" data-testid="next-project-link">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-white/40 mb-3">{t({ es: "Siguiente proyecto", en: "Next project" })}</p>
          <div className="flex items-center justify-between gap-6">
            <h2 className="font-display text-3xl md:text-5xl font-black text-white group-hover:text-brand-gold transition-colors">{next.title}</h2>
            <ArrowRight size={40} className="text-white/40 group-hover:text-brand-gold group-hover:translate-x-3 transition-all shrink-0" />
          </div>
        </Link>
      </section>
    </div>
  );
};

export default ProyectoDetalle;
